import logger from '@/utils/logger';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Search, Filter, Loader2, Mail, ArrowLeft, RefreshCw, RotateCcw } from 'lucide-react';

interface WaitlistEntry {
 id: string;
 email: string;
 full_name: string;
 company_name: string | null;
 motivation: string | null;
 status: 'pending' | 'approved' | 'rejected';
 invitation_sent_at: string | null;
 created_at: string;
 notes: string | null;
}

export default function AdminWaitlist() {
 const [adminKey, setAdminKey] = useState('');
 const [isAuthenticated, setIsAuthenticated] = useState(false);
 const [entries, setEntries] = useState<WaitlistEntry[]>([]);
 const [filteredEntries, setFilteredEntries] = useState<WaitlistEntry[]>([]);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState('');
 const [searchTerm, setSearchTerm] = useState('');
 const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
 const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, beta_slots: { slots_used: 0, slots_remaining: 100 } });
 const [actionLoading, setActionLoading] = useState<string | null>(null); // Email en cours de traitement
 const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

 // Charger les entrées
 const fetchEntries = async () => {
 if (!adminKey) return;

 setLoading(true);
 setError('');

 try {
 // Utiliser l'URL backend appropriée selon l'environnement
 const backendUrl = window.location.hostname === 'localhost'
 ? 'http://localhost:3000'
 : 'https://clairifybackendaqetdhg6-container-nostalgic-swanson.functions.fnc.fr-par.scw.cloud';
 
 const response = await fetch(`${backendUrl}/api/waitlist/list?adminKey=${encodeURIComponent(adminKey)}`);
 const data = await response.json();

 if (!response.ok) {
 throw new Error(data.error || 'Erreur de chargement');
 }

 setEntries(data.data || []);
 setFilteredEntries(data.data || []);
 setStats(data.stats || { total: 0, pending: 0, approved: 0, rejected: 0, beta_slots: { slots_used: 0, slots_remaining: 100 } });
 setIsAuthenticated(true);
 logger.log(' Entrées waitlist chargées:', data.data.length);
 
 // Afficher le compteur beta
 if (data.stats?.beta_slots) {
 logger.log(` Slots beta: ${data.stats.beta_slots.slots_used}/100 utilisés, ${data.stats.beta_slots.slots_remaining} restants`);
 }
 } catch (error: any) {
 logger.error(' Erreur chargement waitlist:', error);
 setError(error.message || 'Erreur de chargement');
 setIsAuthenticated(false);
 } finally {
 setLoading(false);
 }
 };

 // Approuver une demande
 const handleApprove = async (email: string) => {
 if (!window.confirm(`Approuver ${email} et envoyer l'invitation ?`)) {
 return;
 }

 setActionLoading(email);
 setNotification(null);

 try {
 const backendUrl = window.location.hostname === 'localhost'
 ? 'http://localhost:3000'
 : 'https://clairifybackendaqetdhg6-container-nostalgic-swanson.functions.fnc.fr-par.scw.cloud';
 
 const response = await fetch(`${backendUrl}/api/waitlist/approve`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ email, adminKey }),
 });

 const data = await response.json();

 if (!response.ok) {
 throw new Error(data.error || 'Erreur d\'approbation');
 }

 logger.log(' Demande approuvée:', email);
 
 // Afficher notification de succès
 const slotsInfo = data.slots_remaining !== undefined 
 ? ` (${data.slots_remaining} places restantes)`
 : '';
 setNotification({ 
 type: 'success', 
 message: ` Email d'invitation envoyé à ${email}${slotsInfo}` 
 });
 
 // Recharger après 1 seconde
 setTimeout(() => {
 fetchEntries();
 setNotification(null);
 }, 2000);
 } catch (error: any) {
 logger.error(' Erreur approbation:', error);
 setNotification({ 
 type: 'error', 
 message: ` Erreur: ${error.message}` 
 });
 } finally {
 setActionLoading(null);
 }
 };

 // Renvoyer l'invitation à un utilisateur déjà approuvé
 const handleResendInvitation = async (email: string) => {
 if (!window.confirm(`Renvoyer l'invitation à ${email} ?`)) {
 return;
 }

 setActionLoading(email);
 setNotification(null);

 try {
 const backendUrl = window.location.hostname === 'localhost'
 ? 'http://localhost:3000'
 : 'https://clairifybackendaqetdhg6-container-nostalgic-swanson.functions.fnc.fr-par.scw.cloud';
 
 const response = await fetch(`${backendUrl}/api/waitlist/resend-invitation`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ email, adminKey }),
 });

 const data = await response.json();

 if (!response.ok) {
 throw new Error(data.error || 'Erreur de renvoi');
 }

 logger.log('✅ Invitation renvoyée:', email);
 setNotification({ 
 type: 'success', 
 message: `✅ Invitation renvoyée à ${email}` 
 });
 
 setTimeout(() => {
 fetchEntries();
 setNotification(null);
 }, 2000);
 } catch (error: any) {
 logger.error('❌ Erreur renvoi invitation:', error);
 setNotification({ 
 type: 'error', 
 message: `❌ Erreur: ${error.message}` 
 });
 } finally {
 setActionLoading(null);
 }
 };

 // Réinitialiser un utilisateur à pending
 const handleResetToPending = async (email: string) => {
 if (!window.confirm(`Réinitialiser ${email} à "En attente" ? Cela supprimera son token d'invitation actuel.`)) {
 return;
 }

 setActionLoading(email);
 setNotification(null);

 try {
 const backendUrl = window.location.hostname === 'localhost'
 ? 'http://localhost:3000'
 : 'https://clairifybackendaqetdhg6-container-nostalgic-swanson.functions.fnc.fr-par.scw.cloud';
 
 const response = await fetch(`${backendUrl}/api/waitlist/reset-to-pending`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ email, adminKey }),
 });

 const data = await response.json();

 if (!response.ok) {
 throw new Error(data.error || 'Erreur de réinitialisation');
 }

 logger.log('✅ Utilisateur réinitialisé:', email);
 setNotification({ 
 type: 'success', 
 message: `✅ ${email} réinitialisé à "En attente"` 
 });
 
 setTimeout(() => {
 fetchEntries();
 setNotification(null);
 }, 2000);
 } catch (error: any) {
 logger.error('❌ Erreur réinitialisation:', error);
 setNotification({ 
 type: 'error', 
 message: `❌ Erreur: ${error.message}` 
 });
 } finally {
 setActionLoading(null);
 }
 };

 // Rejeter une demande
 const handleReject = async (email: string) => {
 const reason = window.prompt(`Raison du rejet (optionnel) :`);
 if (reason === null) return; // Annulé

 setActionLoading(email);
 setNotification(null);

 try {
 const backendUrl = window.location.hostname === 'localhost'
 ? 'http://localhost:3000'
 : 'https://clairifybackendaqetdhg6-container-nostalgic-swanson.functions.fnc.fr-par.scw.cloud';
 
 const response = await fetch(`${backendUrl}/api/waitlist/reject`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ email, adminKey, reason }),
 });

 const data = await response.json();

 if (!response.ok) {
 throw new Error(data.error || 'Erreur de rejet');
 }

 logger.log(' Demande rejetée:', email);
 setNotification({ 
 type: 'success', 
 message: `Demande de ${email} rejetée` 
 });
 
 setTimeout(() => {
 fetchEntries();
 setNotification(null);
 }, 2000);
 } catch (error: any) {
 logger.error(' Erreur rejet:', error);
 setNotification({ 
 type: 'error', 
 message: ` Erreur: ${error.message}` 
 });
 } finally {
 setActionLoading(null);
 }
 };

 // Filtrer les entrées
 useEffect(() => {
 let filtered = entries;

 // Filtrer par statut
 if (statusFilter !== 'all') {
 filtered = filtered.filter((e) => e.status === statusFilter);
 }

 // Filtrer par recherche
 if (searchTerm) {
 const term = searchTerm.toLowerCase();
 filtered = filtered.filter(
 (e) =>
 e.email.toLowerCase().includes(term) ||
 e.full_name.toLowerCase().includes(term) ||
 e.company_name?.toLowerCase().includes(term)
 );
 }

 setFilteredEntries(filtered);
 }, [searchTerm, statusFilter, entries]);

 // Authentification
 if (!isAuthenticated) {
 return (
 <div className="min-h-screen bg-theme-base flex items-center justify-center p-4">
 <div className="w-full max-w-md">
 <div className="bg-theme-surface rounded-2xl shadow-2xl p-8 border border-theme-border">
 <h2 className="text-2xl font-bold text-theme-primary mb-6 text-center">
 Admin Waitlist
 </h2>
 <form
 onSubmit={(e) => {
 e.preventDefault();
 fetchEntries();
 }}
 className="space-y-4"
 >
 <div>
 <label className="block text-sm font-medium text-theme-primary mb-2">
 Clé Admin
 </label>
 <input
 type="password"
 value={adminKey}
 onChange={(e) => setAdminKey(e.target.value)}
 className="w-full px-4 py-3 bg-theme-base border border-theme-border rounded-lg text-theme-primary focus:outline-none focus:ring-2 focus:ring-accent-primary"
 placeholder="Entrez la clé admin"
 required
 />
 </div>
 {error && (
 <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
 <p className="text-sm text-red-500">{error}</p>
 </div>
 )}
 <button
 type="submit"
 disabled={loading}
 className="w-full bg-gradient-to-r from-accent-primary to-accent-secondary text-white font-semibold py-3 px-6 rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
 >
 {loading ? 'Connexion...' : 'Se connecter'}
 </button>
 </form>
 <div className="mt-6 text-center">
 <Link to="/" className="text-sm text-theme-secondary hover:text-accent-primary transition-colors">
 ← Retour à l'accueil
 </Link>
 </div>
 </div>
 </div>
 </div>
 );
 }

 // Interface admin
 return (
 <div className="min-h-screen bg-theme-base p-4 md:p-8">
 <div className="max-w-7xl mx-auto">
 {/* Header */}
 <div className="mb-8">
 <Link to="/" className="inline-flex items-center gap-2 text-theme-secondary hover:text-accent-primary mb-4">
 <ArrowLeft className="w-4 h-4" />
 Retour à l'accueil
 </Link>
 <h1 className="text-3xl font-bold text-theme-primary mb-2">Gestion Waitlist Beta</h1>
 <p className="text-theme-secondary">Approuvez les demandes d'accès et envoyez les invitations</p>
 </div>

 {/* Notification */}
 {notification && (
 <div className={`mb-6 p-4 rounded-lg border ${
 notification.type === 'success' 
 ? 'bg-green-500/10 border-green-500/20 text-green-500'
 : 'bg-red-500/10 border-red-500/20 text-red-500'
 }`}>
 <p className="font-medium">{notification.message}</p>
 </div>
 )}

 {/* Stats */}
 <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
 <div className="bg-theme-surface p-4 rounded-lg border border-theme-border">
 <p className="text-theme-tertiary text-sm mb-1">Total</p>
 <p className="text-2xl font-bold text-theme-primary">{stats.total}</p>
 </div>
 <div className="bg-theme-surface p-4 rounded-lg border border-theme-border">
 <p className="text-theme-tertiary text-sm mb-1">En attente</p>
 <p className="text-2xl font-bold text-yellow-500">{stats.pending}</p>
 </div>
 <div className="bg-theme-surface p-4 rounded-lg border border-theme-border">
 <p className="text-theme-tertiary text-sm mb-1">Approuvées</p>
 <p className="text-2xl font-bold text-green-500">{stats.approved}</p>
 </div>
 <div className="bg-theme-surface p-4 rounded-lg border border-theme-border">
 <p className="text-theme-tertiary text-sm mb-1">Rejetées</p>
 <p className="text-2xl font-bold text-red-500">{stats.rejected}</p>
 </div>
 <div className="bg-theme-surface p-4 rounded-lg border border-theme-border">
 <p className="text-theme-tertiary text-sm mb-1"> Slots Beta</p>
 <p className="text-2xl font-bold text-[var(--spark-gold)]">
 {stats.beta_slots?.slots_remaining || 100}/100
 </p>
 <p className="text-xs text-theme-tertiary mt-1">places restantes</p>
 </div>
 </div>

 {/* Filtres */}
 <div className="bg-theme-surface p-4 rounded-lg border border-theme-border mb-6">
 <div className="flex flex-col md:flex-row gap-4">
 {/* Recherche */}
 <div className="flex-1">
 <div className="relative">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-tertiary w-5 h-5" />
 <input
 type="text"
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 placeholder="Rechercher par email, nom ou entreprise..."
 className="w-full pl-10 pr-4 py-2 bg-theme-base border border-theme-border rounded-lg text-theme-primary focus:outline-none focus:ring-2 focus:ring-accent-primary"
 />
 </div>
 </div>

 {/* Filtre statut */}
 <div className="flex gap-2">
 <button
 onClick={() => setStatusFilter('all')}
 className={`px-4 py-2 rounded-lg font-medium transition-all ${
 statusFilter === 'all'
 ? 'bg-accent-primary text-white'
 : 'bg-theme-base text-theme-secondary hover:bg-theme-base/50'
 }`}
 >
 Tous
 </button>
 <button
 onClick={() => setStatusFilter('pending')}
 className={`px-4 py-2 rounded-lg font-medium transition-all ${
 statusFilter === 'pending'
 ? 'bg-yellow-500 text-white'
 : 'bg-theme-base text-theme-secondary hover:bg-theme-base/50'
 }`}
 >
 En attente
 </button>
 <button
 onClick={() => setStatusFilter('approved')}
 className={`px-4 py-2 rounded-lg font-medium transition-all ${
 statusFilter === 'approved'
 ? 'bg-green-500 text-white'
 : 'bg-theme-base text-theme-secondary hover:bg-theme-base/50'
 }`}
 >
 Approuvées
 </button>
 </div>

 {/* Bouton refresh */}
 <button
 onClick={fetchEntries}
 disabled={loading}
 className="px-4 py-2 bg-theme-base text-theme-secondary hover:bg-theme-base/50 rounded-lg transition-all disabled:opacity-50"
 >
 {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : ''}
 </button>
 </div>
 </div>

 {/* Liste */}
 <div className="bg-theme-surface rounded-lg border border-theme-border overflow-hidden">
 {filteredEntries.length === 0 ? (
 <div className="p-8 text-center text-theme-secondary">
 Aucune demande trouvée
 </div>
 ) : (
 <div className="divide-y divide-theme-border">
 {filteredEntries.map((entry) => (
 <div key={entry.id} className="p-4 hover:bg-theme-base/50 transition-colors">
 <div className="flex items-start justify-between gap-4">
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-2">
 <h3 className="font-semibold text-theme-primary truncate">{entry.full_name}</h3>
 <span
 className={`px-2 py-0.5 rounded text-xs font-medium ${
 entry.status === 'pending'
 ? 'bg-yellow-500/10 text-yellow-500'
 : entry.status === 'approved'
 ? 'bg-green-500/10 text-green-500'
 : 'bg-red-500/10 text-red-500'
 }`}
 >
 {entry.status === 'pending' && '⏳ En attente'}
 {entry.status === 'approved' && ' Approuvée'}
 {entry.status === 'rejected' && ' Rejetée'}
 </span>
 </div>
 <p className="text-sm text-theme-secondary mb-1">{entry.email}</p>
 {entry.company_name && (
 <p className="text-sm text-theme-tertiary mb-1"> {entry.company_name}</p>
 )}
                {entry.motivation && (
                  <p className="text-sm text-theme-secondary mt-2 italic">"{entry.motivation}"</p>
                )}
                <p className="text-xs text-theme-tertiary mt-2">
                  Demandé le {new Date(entry.created_at).toLocaleDateString('fr-FR')}
                </p>
                {entry.invitation_sent_at && (
                  <p className="text-xs text-green-600 mt-1">
                    ✉️ Invitation envoyée le {new Date(entry.invitation_sent_at).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>

              {/* Actions */}
              {entry.status === 'pending' && (
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => handleApprove(entry.email)}
                    disabled={actionLoading === entry.email}
                    className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Approuver et envoyer invitation"
                  >
                    {actionLoading === entry.email ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Envoi...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Approuver
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleReject(entry.email)}
                    disabled={actionLoading === entry.email}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Rejeter la demande"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {entry.status === 'approved' && (
                <div className="flex flex-col gap-2">
                  <div className="text-sm text-green-500 flex items-center gap-2 px-3 py-2 bg-green-500/10 rounded-lg">
                    <Mail className="w-4 h-4" />
                    <span className="font-medium">Invitation envoyée</span>
                  </div>
                  <button
                    onClick={() => handleResendInvitation(entry.email)}
                    disabled={actionLoading === entry.email}
                    className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    title="Renvoyer l'email d'invitation"
                  >
                    {actionLoading === entry.email ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>
                        <RefreshCw className="w-3 h-3" />
                        Renvoyer
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleResetToPending(entry.email)}
                    disabled={actionLoading === entry.email}
                    className="px-3 py-1.5 bg-[var(--theme-text-primary)] text-[var(--theme-text-inverse)] text-sm disabled:opacity-50"
                    title="Réinitialiser à 'En attente'"
                  >
                    {actionLoading === entry.email ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>
                        <RotateCcw className="w-3 h-3" />
                        Réinit.
                      </>
                    )}
                  </button>
                </div>
              )}
              
              {entry.status === 'rejected' && (
                <div className="flex flex-col gap-2">
                  <div className="text-sm text-red-500 flex items-center gap-2 px-3 py-2 bg-red-500/10 rounded-lg">
                    <XCircle className="w-4 h-4" />
                    <span className="font-medium">Rejetée</span>
                  </div>
                  <button
                    onClick={() => handleResetToPending(entry.email)}
                    disabled={actionLoading === entry.email}
                    className="px-3 py-1.5 bg-[var(--theme-text-primary)] text-[var(--theme-text-inverse)] text-sm disabled:opacity-50"
                    title="Réinitialiser à 'En attente'"
                  >
                    {actionLoading === entry.email ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>
                        <RotateCcw className="w-3 h-3" />
                        Réinit.
                      </>
                    )}
                  </button>
                </div>
              )}
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 </div>
 </div>
 );
}

